import { IconAlertTriangle, IconCircleCheck, IconFileInvoice, IconLayoutDashboard } from "@tabler/icons-react";
import { useMisEstadisticas } from "../hooks/use-mis-estadisticas";
import { StatCard } from "./StatCard";

/**
 * Grilla de los 4 KPIs principales del dashboard.
 *
 * Lee de `/api/solicitudes/mis-estadisticas` (polling 30s). Los labels y
 * los hints se adaptan al `audiencia` que retorna el backend:
 *   · "Solicitante" · KPIs sobre SUS solicitudes (las que él creó).
 *   · "Autorizador" · KPIs sobre lo que tiene que autorizar / autorizó.
 *   · "Admin" (Auditor/Admin/MesaDeControl) · conteos globales.
 *   · "Visor" · conteos globales de solo lectura (labels de panorama).
 */
export function StatsGrid() {
  const { data, isPending, isError } = useMisEstadisticas();

  const stats = data ?? null;
  const errorHint = isError ? "Sin conexión · reintentando" : undefined;
  const esAutorizador = stats?.audiencia === "Autorizador";
  const esVisor = stats?.audiencia === "Visor";

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label={esAutorizador ? "En mi bandeja" : "Solicitudes activas"}
        value={stats ? String(stats.solicitudesActivas) : "—"}
        hint={
          errorHint ??
          (esAutorizador
            ? stats && stats.solicitudesActivas > 0
              ? "Esperando tu decisión"
              : "Sin pendientes"
            : stats?.enFlujoAutorizacion
              ? `${stats.enFlujoAutorizacion} en autorización`
              : esVisor
                ? "Activas en el sistema"
                : "En proceso ahora")
        }
        icon={IconFileInvoice}
        color="slate"
        to={esAutorizador ? "/autorizacion/bandeja" : "/solicitudes"}
        sparkShape="a"
        serieColor="var(--color-multiva-600)"
        loading={isPending}
      />
      <StatCard
        label={esAutorizador ? "Requieren mi autorización" : esVisor ? "Requieren corrección" : "Requieren mi acción"}
        value={stats ? String(stats.requierenMiAccion) : "—"}
        hint={
          errorHint ??
          (esAutorizador
            ? stats && stats.requierenMiAccion > 0
              ? "Abre la bandeja"
              : "Todo al día"
            : esVisor
              ? stats && stats.requierenMiAccion > 0
                ? "Rechazadas · pendientes del solicitante"
                : "Sin rechazos pendientes"
              : stats && stats.requierenMiAccion > 0
                ? "Corrige y reenvía"
                : "Todo al día")
        }
        icon={IconAlertTriangle}
        // Drill-through: con pendientes por corregir la tarjeta navega a la lista
        // filtrada por "requieren revisión". El chip usa `slate` como el resto de
        // los KPIs (consistencia visual) y el número siempre en negro.
        color="slate"
        to={esAutorizador ? "/autorizacion/bandeja" : "/solicitudes"}
        search={!esAutorizador && stats && stats.requierenMiAccion > 0 ? { filtro: "requierenAccion" } : undefined}
        sparkShape="c"
        serieColor="var(--color-advertencia-500)"
        loading={isPending}
      />
      <StatCard
        label={esAutorizador ? "Autorizadas por mí (7 días)" : "Autorizadas (7 días)"}
        value={stats ? String(stats.autorizadasUltimos7Dias) : "—"}
        delta={stats?.deltaAutorizadas ?? null}
        deltaLabel="vs semana previa"
        hint={errorHint ?? "Sin actividad reciente"}
        icon={IconCircleCheck}
        color="slate"
        sparkShape="b"
        serieColor="var(--color-verde-600)"
        loading={isPending}
      />
      <StatCard
        label="Pólizas generadas"
        value={stats ? String(stats.polizasGeneradas) : "—"}
        hint={
          errorHint ??
          (esAutorizador
            ? stats && stats.polizasGeneradas > 0
              ? "De solicitudes que autorizé"
              : "Histórico"
            : stats && stats.polizasGeneradas > 0
              ? "Histórico total"
              : "Histórico")
        }
        icon={IconLayoutDashboard}
        color="slate"
        sparkShape="d"
        // Azul (info) · distinto del slate que usa "Próximas a vencer".
        serieColor="var(--color-info-600)"
        loading={isPending}
      />
    </div>
  );
}
